// Sync find-references service — Phase 3 Slices 24 + 25 + 26.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 24 (column), § Slice 25
// (table/view), § Slice 26 (routine).
//
// Reads the connection's IdentifierIndex (built in Phase 2 — Slice 23 owns the
// per-connection lifecycle), groups the matching ReferenceRecord[] by object,
// and returns a `FindReferencesResult`. Pure on the inputs (apart from the
// lifecycle lookup); no async I/O — the index is in-memory.
//
// The lifecycle accessor is dependency-injected so unit tests don't need to
// stand up the connection store. Production code calls `findReferences(...)`
// directly with the default `getIndex` that reads the live lifecycle.
//
// Slice 25 note: at index-build time the SELECT-body walker can't tell whether
// a `FROM <name>` target resolves to a table or a view (the OE catalog isn't
// consulted), so it always records relation refs under
// `identifierKind: 'table'`. Find-references therefore treats `'table'` and
// `'view'` as a unified relation kind: a request for either kind queries the
// `'table'` bucket and the `'view'` bucket and merges the results.

import {
  getIdentifierIndexFor as defaultGetIdentifierIndexFor,
} from '../connection-lifecycle/navigationalLifecycle';
import { FOLD_BOTH, objectKeyFor } from '../index/identifierIndex';
import type { ObjectKeyCasePolicy } from '../rename/refGrouper';
import {
  catalogLeafRefMatches,
  effectiveDatabaseFor,
  matchRelationRef,
  mssqlCatalogCaseSensitiveFor,
  relationIdentityContextFor,
} from '../relationIdentity';
import { matchRoutineRef, routineIdentityContextFor } from '../routineIdentity';
import { mssqlDatabaseNamesCaseSensitive } from '../mssqlCollationProbe';
import { resolveDefaultSchemaForEngine } from '../../workspace/common/utils/defaultSchemaForEngine';
import type { DatabaseEngineId } from '@/core/engines';
import type { SqlEngine } from '../../sql-grammar/model';

import type {
  FindReferencesRequest,
  FindReferencesResult,
  FindReferencesResultGroup,
  IdentifierIndex,
  IdentifierKind,
  QualifiedName,
  ReferenceRecord,
} from '../types';

export type FindReferencesDeps = {
  readonly getIndex?: (connectionId: string, database?: string) => IdentifierIndex | null;
  /** The database whose index to read — the Object-Explorer node's / query
   *  tab's database. The lifecycle holds one index per (connection, database)
   *  on multi-database engines; omitted → the connection's configured
   *  database. Callers await `indexReadyFor(connectionId, database)` first so
   *  the index exists and is built. */
  readonly database?: string;
  /** Engine of the connection — drives the identity comparator's per-engine
   *  case rules (#34). Omitted in unit tests: defaults to `mssql`
   *  (case-insensitive), which maximises discovery over-inclusion when the
   *  engine is unknown. The production caller (`openReferencesTab`) threads the
   *  connection engine. */
  readonly engine?: SqlEngine;
  /** MySQL `@@lower_case_table_names` (0/1/2) — task-review finding 1: threaded
   *  from `mysqlCaseProbe.ts`'s `getMysqlLowerCaseTableNamesFor` (which reads
   *  the already-cached probe from `mysqlSchemaCache.ts`) by the production
   *  caller (`openReferencesTab`). Undefined → case-insensitive identity
   *  matching (see `relationIdentity.ts`'s documented default). Ignored for
   *  non-mysql engines. */
  readonly mysqlLowerCaseTableNames?: number;
  /** MSSQL SERVER (instance) collation — true = the instance tells the
   *  database `Sales` from the database `sales`. Omitted (the normal case) →
   *  read from the connection's probed instance collation
   *  (`mssqlCollationProbe.ts`); unit tests pass it directly. A DIFFERENT
   *  collation from the per-database one the index handle reports, which
   *  governs object/schema names. */
  readonly mssqlDatabaseNamesCaseSensitive?: boolean;
};

export function findReferences(
  connectionId: string,
  identifier: QualifiedName,
  kind: IdentifierKind,
  deps?: FindReferencesDeps,
): FindReferencesResult {
  const getIndex = deps?.getIndex ?? defaultGetIdentifierIndexFor;
  const index = getIndex(connectionId, deps?.database);
  const request: FindReferencesRequest = { target: identifier, kind };

  if (!index) {
    return { request, groups: [] };
  }

  const engine = deps?.engine ?? 'mssql';
  const records = collectRecords(
    index,
    identifier,
    kind,
    engine,
    deps?.mysqlLowerCaseTableNames,
    engine === 'mssql'
      ? (deps?.mssqlDatabaseNamesCaseSensitive ?? mssqlDatabaseNamesCaseSensitive(connectionId))
      : undefined,
    // What a BARE routine call in a body resolves to on this connection. The
    // per-engine rule has exactly ONE implementation in the app — MySQL's
    // schema-is-a-database, Oracle's user-is-a-schema and PostgreSQL's
    // `search_path` tail all live there — so it is read, never restated. See
    // `routineIdentity.ts`'s `defaultRoutineSchema` for the table it encodes.
    kind === 'routine'
      ? resolveDefaultSchemaForEngine(engineIdFor(engine), deps?.database, connectionId)
      : undefined,
  );
  // Group by the SAME object-key rule the index used, so two objects the index
  // keeps apart (MySQL `DbA.P` / `dba.P`, a SQL Server `_CS_` database's
  // `dbo.V` / `dbo.v`) stay two groups here — one group is one body in the tab.
  // The rule comes from the INDEX, never recomputed from `(connectionId,
  // engine, deps.database)`: the lifecycle resolves an empty `database` to the
  // connection's configured one and files SQL Server's per-database collation
  // under THAT identity, so recomputing it for `''` looked up a database
  // nothing had probed, answered "unknown" and folded the two views back
  // together.
  // A budget-clipped index can only under-report, so the flag travels with
  // the result rather than being re-derived by every consumer.
  return {
    request,
    groups: groupByObject(records, index.objectKeyCaseFor),
    partial: index.isPartial(),
  };
}

/** `SqlEngine` (the parser's vocabulary) → `DatabaseEngineId` (the app's). A
 *  shape adapter, not a second engine table: only PostgreSQL is spelled
 *  differently between the two. */
function engineIdFor(engine: SqlEngine): DatabaseEngineId {
  return engine === 'postgres' ? 'pgsql' : engine;
}

function collectRecords(
  index: IdentifierIndex,
  identifier: QualifiedName,
  kind: IdentifierKind,
  engine: SqlEngine,
  mysqlLowerCaseTableNames: number | undefined,
  mssqlDatabaseNamesCaseSensitiveValue: boolean | undefined,
  defaultRoutineSchema: string | undefined,
): readonly ReferenceRecord[] {
  // One identity context for both branches: SQL Server's per-database collation
  // comes off the INDEX handle — the rule the lifecycle probed before it
  // created that index — for the same reason the grouping above does (see
  // `mssqlCatalogCaseSensitiveFor`). The instance collation that governs
  // DATABASE names is a different probe and travels alongside it.
  const ctx = relationIdentityContextFor(engine, {
    mysqlLowerCaseTableNames,
    mssqlCatalogCaseSensitive: mssqlCatalogCaseSensitiveFor(index, engine),
    mssqlDatabaseNamesCaseSensitive: mssqlDatabaseNamesCaseSensitiveValue,
  });
  if (kind === 'table' || kind === 'view') {
    // #34: candidate retrieval is broad (leaf-name lookup covering both the
    // `'table'` and `'view'` buckets), then the engine-aware comparator drops
    // definite-different relations. Unlike rename, find-references KEEPS
    // ambiguous (unqualified) matches — safe over-inclusion is the right
    // discovery default; the user resolves ambiguity. A differing explicit
    // schema / database is the only reason to exclude a candidate.
    // SQL Server's half of the same question: in a `_CS_` database `dbo.V` and
    // `dbo.v` are two objects, so a search for one must not list the other's
    // references.
    // Only the target's OWN explicit database (never the tab's) narrows a
    // search — as it always has. `effectiveDatabaseFor` preserves its SPELLED
    // half so a `_CS_` SQL Server instance can tell `Sales` from `sales`.
    const effectiveDb = effectiveDatabaseFor(identifier, undefined, ctx);
    return index
      .queryRelationCandidates(identifier)
      .filter((r) => matchRelationRef(r.identifier, r.refPartCount, identifier, effectiveDb, ctx) !== null);
  }
  if (kind === 'routine') {
    // A routine's SCHEMA is part of its identity (unlike a column's prefix,
    // which is alias noise), so the exact-match key keeps it — and that is
    // exactly why the exact key cannot be the DISCOVERY key here: the emitters
    // record the call as the body spelled it (`fn(x)` bare vs `dbo.fn(x)`)
    // while the reader arrives with the target's qualification (an
    // Object-Explorer routine is always schema-qualified). An OE "Find
    // References" on `dbo.fn` therefore missed every view calling `fn(...)`
    // bare, and a cursor on a bare `fn(` missed every body calling `dbo.fn`.
    // Same shape as the relation fix above: over-collect on the leaf, then let
    // the engine-aware comparator drop the definite-different schemas /
    // databases. `callerObject` is the record's own object, so a bare call
    // inside `sales.v_orders` can resolve to `sales.fn`.
    const routineCtx = routineIdentityContextFor(ctx, defaultRoutineSchema);
    return index
      .queryRelationCandidates(identifier, 'routine')
      .filter((r) => matchRoutineRef(r.identifier, identifier, r.objectName, routineCtx) !== null)
      .filter((r) => catalogLeafRefMatches(r.identifier, identifier, kind, ctx));
  }
  // Columns come from the exact-match lookup, whose key is lowercased on
  // purpose (a discovery index — `index/identifierIndex.ts`) and bare: the
  // cursor hands this service a schema/database-qualified column
  // (`referenceTargetAtCursor.ts` → `columnHit.binding.column`) while every
  // emitter records it bare, so the index drops the qualification on both
  // sides. Without that, Shift+F12 and Peek reported "no references" for a
  // column the index held.
  // The per-engine identity decision is then applied here: SQL Server's
  // per-database collation governs column names too, so a `_CS_` database's
  // `dbo.p` must not answer a search for `dbo.P`; and on PostgreSQL / Oracle
  // the folded form IS the identity, so a quoted `"Id"` must not answer a
  // search for `id`. Both are `catalogLeafRefMatches` — a no-op on MySQL,
  // SQLite and a `_CI_` SQL Server database.
  return index
    .query(identifier, kind)
    .filter((r) => catalogLeafRefMatches(r.identifier, identifier, kind, ctx));
}

function groupByObject(
  records: readonly ReferenceRecord[],
  keyCase: ObjectKeyCasePolicy,
): readonly FindReferencesResultGroup[] {
  // Stable group order = first-record-wins; refs within a group preserve
  // the index's insertion order which mirrors the source body order.
  const buckets = new Map<string, ReferenceRecord[]>();
  const headers = new Map<
    string,
    { readonly objectKind: ReferenceRecord['objectKind']; readonly objectName: QualifiedName }
  >();

  for (const ref of records) {
    const key = objectKeyFor(ref.objectKind, ref.objectName, keyCase(ref.objectKind) ?? FOLD_BOTH);
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = [];
      buckets.set(key, bucket);
      headers.set(key, { objectKind: ref.objectKind, objectName: ref.objectName });
    }
    bucket.push(ref);
  }

  const groups: FindReferencesResultGroup[] = [];
  for (const [key, refs] of buckets) {
    const header = headers.get(key);
    if (!header) continue;
    groups.push({
      objectKind: header.objectKind,
      objectName: header.objectName,
      references: refs,
    });
  }
  return groups;
}
